import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { Map } from 'mapbox-gl';
import React, { createContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MillPolygon } from "../../api/api";
import { GetMillsForBids } from '../../api/orgs';
import { Loader } from "../../components/Loader";
import MapBox from "../../components/MapBox";
import { useAppContext } from '../../contexts/AppContext';
import { defaultLat, defaultLng, isNullOrUndefined, showErrorMessage } from "../../helper/helper";
import { buildDataForPricePage, updatePolygonMill } from "../../models/MapBoxDataModel";
import { Organisation, OrganisationPermission } from '../../models/organisation/OrganisationModelV2';
import { MillPriceOnMap } from '../../models/price/MillPriceOnMap';
import { PriceContainer } from './component/PriceContainer';
import { MapMillPrice } from './map/MapMillPrice';
import './MyPrices.scss';

export const PRICE_CODE_CAN_PUBLISH_PRICE_DEMAND = 0
export const PRICE_CODE_CAN_PUBLISH_PRICE_NOT_DEMAND = 1
export const PRICE_CODE_MARKET_CLOSE = 2
export const PRICE_CODE_NO_ORG_LOCATION = 3

type MyPricesProps = {
    client: ApolloClient<NormalizedCacheObject>,
}

/** An organisation the logged-in user is an employee of, with their permissions on it. */
export class OrganisationWithPermission {
    permissions: OrganisationPermission
    organisation: Organisation

    constructor(p: any) {
        this.permissions = p.permissions
        this.organisation = p.organisation
    }

    static build(p: any): OrganisationWithPermission {
        return new OrganisationWithPermission(p)
    }
}

export type MarketContextData = {
    priceSelected?: MillPriceOnMap
    setPriceSelected?: any
}

export const MarketContext = createContext<MarketContextData>({})

export function MyPrices(props: MyPricesProps) {

    const { millSelected } = useAppContext()

    const [myOrgs, setMyOrgs] = useState<OrganisationWithPermission[]>(null)
    const [map, setMap] = useState<Map>(null)
    const [loading, setLoading] = React.useState(false)
    const myOrgsRef = useRef(myOrgs)
    const [selectedPrice, setSelectedPrice] = useState<any>(undefined)
    const [updatedData, setUpdatedData] = useState<number>(0)

    const mapRef = useRef(null)

    const { t } = useTranslation()

    useEffect(() => {
        myOrgsRef.current = myOrgs
    }, [myOrgs])

    useEffect(() => {
        if (millSelected && mapRef.current) {
            fetchMillPolygon(millSelected.id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [millSelected])

    useEffect(() => {
        fetchMyOrgs()
    }, [])

    function fetchMillPolygon(id: number) {
        props.client.query({
            query: MillPolygon,
            variables: {
                inputId: id
            }
        }).then(result => {
            const coords = result.data.coords
            updatePolygonMill(mapRef.current, coords)
        }).catch(e => showError(e))
    }

    function fetchMyOrgs() {
        setLoading(true)
        props.client.query({
            query: GetMillsForBids
        }).then(result => {
            const profile = result.data?.profile?.profile
            const orgs: Organisation[] = profile?.organisations?.map((o) => Organisation.build(o)) ?? []
            const orgsWithPermission: OrganisationWithPermission[] = profile?.acl?.organisation_permissions
                ?.map((p) => OrganisationPermission.build(p))
                ?.map((perm: OrganisationPermission) => {
                    const org = orgs.find(o => o?.id === perm?.organisation_id)
                    return org ? OrganisationWithPermission.build({
                        permissions: perm,
                        organisation: org
                    }) : undefined
                })
                ?.filter(o => !isNullOrUndefined(o?.organisation?.name)) ?? []
            setMyOrgs(orgsWithPermission.sort((a, b) => a.organisation.name.localeCompare(b.organisation.name)))
            setLoading(false)
        }).catch(e => showError(e))
    }

    function showError(message: any) {
        showErrorMessage(message)
        setLoading(false)
    }

    function reload() {
        setUpdatedData(updatedData + 1)
    }

    const getPriceForCompany = () => {
        return (
            <div style={{ display: "flex", flexDirection: "column" }}>
                {myOrgs.filter(o => o.organisation.id === millSelected.id).map((org) => {
                    return <PriceContainer key={org.organisation.id} onPriceUpdated={reload} orgParam={org}/>
                }
                )}
            </div>
        )
    }

    const showEmptyView = (text) => {
        return (
            <div style={{ textAlign: "center", marginTop: "60px" }}>
                <p style={{ padding: "20px", border: "2px dashed black", display: "inline-block", margin: "0px" }}>{text}</p>
            </div>
        )
    }

    function getCoord() {
        const coord = millSelected?.position?.coordinates
        if (coord) {
            return coord
        }
        return [defaultLng, defaultLat]
    }

    function onMapLoaded(map: Map) {
        setMap(map)
    }

    useEffect(() => {
        if (myOrgs && map) {
            mapRef.current = map;
            buildDataForPricePage(map).then(() => {
            })
        }
    }, [map, myOrgs])

    return (
        <MarketContext.Provider
            value={{
                priceSelected: selectedPrice,
                setPriceSelected: setSelectedPrice
            }}>
            <div style={{ flex: "1" }}>
                <div className='pempem-flex-col header'>
                    <h1>{t("my_prices")}</h1>
                </div>
                {/* <MarketTimeLine currentTurn={currentTurn} /> */}
                <div className="prices-map-container">
                    <div className='price-list' style={{ marginLeft: "28px", marginRight: "28px" }}>
                        {myOrgs && millSelected ?
                            getPriceForCompany()
                            : !loading ? showEmptyView(t('no_price_to_show')) : <></>
                        }
                        <Loader isLoading={loading} />
                    </div>
                    <div className="map-container">
                        <div className="map-container-ar">
                            {myOrgs && <MapBox lng={millSelected?.position?.coordinates?.at(0) ?? defaultLng} lat={millSelected?.position?.coordinates?.at(1) ?? defaultLat} width="100%" height="100%" onLoaded={onMapLoaded} /> }
                            {map &&
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0
                                }}>
                                    <MapMillPrice map={map} orgsParam={myOrgs} updateData={updatedData} />
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </MarketContext.Provider>
    )
}